// Component: AlgoStrategyListItem.jsx
// Purpose: Compact two-line list row for Algo Trading dashboard

import React, { useMemo } from 'react';
import LivePriceBadge from '../../../components/trading/LivePriceBadge';

// Component: Deployment lifecycle reason copy
// Keep machine codes and instrument identifiers off the user-facing surface.
const stopReasonCopy = {
  instrument_expired: 'Stopped — instrument expired',
  instrument_inactive: 'Stopped — instrument no longer tradable',
};

const OPEN_POSITION_COPY = 'Instrument is no longer tradable — close the open position; the deployment will stop automatically';

/*>>> STATUS CONFIGURATION <<<*/
const statusConfig = {
  running: {
    label: 'Running',
    dotClass: 'bg-profit',
    pulse: true,
    textClass: 'text-profit',
  },
  stopped: {
    label: 'Stopped',
    dotClass: 'bg-loss',
    pulse: false,
    textClass: 'text-loss',
  },
};

// Live = profit (green), Paper = info (blue) — accent bar reuses the badge dot token.
const modeConfig = {
  live: {
    label: 'Live',
    accentBorder: 'from-[var(--badge-profit-dot)] to-[var(--badge-profit-dot)]',
  },
  paper: {
    label: 'Paper',
    accentBorder: 'from-[var(--badge-info-dot)] to-[var(--badge-info-dot)]',
  },
};

/*>>> EXTRACT SHORT SYMBOL from instrument_id <<<*/
function extractSymbol(instrumentId) {
  if (!instrumentId) return '';
  const afterColon = instrumentId.includes(':') ? instrumentId.split(':')[1] : instrumentId;
  const match = afterColon.match(/^([A-Z]+?)(?:\d{2}[A-Z]{3}|FUT|CE|PE|\d)/);
  return match ? match[1] : afterColon;
}

/*>>> SPARKLINE SVG (inline, same as existing StrategyRow) <<<*/
function Sparkline({ data = [], width = 80, height = 32, id }) {
  const { polylinePoints, areaPath, isPositive, gradientId } = useMemo(() => {
    if (!data || data.length < 2) {
      return { polylinePoints: '', areaPath: '', isPositive: true, gradientId: `spark-empty-${id}` };
    }
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const padding = 2;
    const effectiveWidth = width - padding * 2;
    const effectiveHeight = height - padding * 2;
    const points = data.map((val, i) => {
      const x = padding + (i / (data.length - 1)) * effectiveWidth;
      const y = padding + effectiveHeight - ((val - min) / range) * effectiveHeight;
      return { x, y };
    });
    const polyline = points.map((p) => `${p.x},${p.y}`).join(' ');
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];
    const area = `M${firstPoint.x},${firstPoint.y} ${points.map((p) => `L${p.x},${p.y}`).join(' ')} L${lastPoint.x},${height} L${firstPoint.x},${height} Z`;
    return { polylinePoints: polyline, areaPath: area, isPositive: data[data.length - 1] >= data[0], gradientId: `spark-${id}` };
  }, [data, width, height, id]);

  if (!data || data.length < 2) {
    return (
      <svg width={width} height={height} className="opacity-30">
        <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="currentColor" strokeWidth="1" strokeDasharray="4 2" className="text-fg-2" />
      </svg>
    );
  }
  const strokeColor = isPositive ? '#00C292' : '#E46A76';
  const fillColor = isPositive ? '#00C292' : '#E46A76';
  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.25" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <polyline points={polylinePoints} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AlgoStrategyListItem({ strategy, onClick }) {
  const {
    id,
    name,
    version,
    mode = 'paper',
    status = 'stopped',
    timeframe,
    instrument,
    //! R5.5/D3 (money-path #30) — the parent already passes the whole strategy object
    //  incl. `.symbol` (resolved_symbol ?? instrument_symbol fallback, AlgoDashboard.jsx
    //  :68-70); this component previously never destructured it and instead ran
    //  extractSymbol() against the raw frozen UUID (:193-195 below), which does not match
    //  the FUT/CE/PE regex and silently rendered a UUID substring. extractSymbol is kept
    //  ONLY as a last-resort legacy fallback for rows with no symbol at all.
    symbol,
    pnl = 0,
    winRate = 0,
    totalTrades = 0,
    openOrdersCount = 0,
    position,
    sparklineData = [],
  } = strategy || {};

  const statusCfg = statusConfig[status] || statusConfig.stopped;
  const modeCfg = modeConfig[mode] || modeConfig.paper;
  const isStopped = status === 'stopped';
  const pnlPositive = pnl >= 0;
  const stopReason = strategy?.stopReason ?? strategy?.stop_reason;
  const deadInstrument = strategy?.deadInstrument ?? strategy?.dead_instrument;
  const statusLabel = (isStopped && stopReasonCopy[stopReason]) || statusCfg.label;
  const showOpenPositionReason = (
    status === 'running'
    && deadInstrument?.reason_code === 'dead_instrument_position_open'
  );

  //! F4 (144-1): a NULL net P&L (charges unresolved) renders as 'Unresolved', never Rs 0.
  const pnlUnresolved = strategy?.pnlUnresolved === true;
  const pnlClass = pnlUnresolved ? 'text-fg-3' : (pnlPositive ? 'text-profit' : 'text-loss');
  const formattedPnl = pnlUnresolved ? 'Unresolved' : `${pnlPositive ? '+' : ''}${pnl.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  })}`;

  //--> Line 2 content
  const secondLine = position
    ? (
      <>
        <span className={`font-semibold ${position.side === 'LONG' ? 'text-profit' : 'text-loss'}`}>
          {position.side}
        </span>
        {' '}{position.qty} @ {'\u20B9'}{position.avg_entry_price?.toLocaleString('en-IN')}
        {' '}&middot;{' '}
        <span className={position.unrealized_pnl >= 0 ? 'text-profit' : 'text-loss'}>
          Unrealized {position.unrealized_pnl >= 0 ? '+' : ''}{'\u20B9'}{position.unrealized_pnl?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </span>
      </>
    )
    : (
      <>{totalTrades} trades &middot; {totalTrades > 0 ? `${(winRate * 100).toFixed(0)}%` : '0%'} win rate &middot; {openOrdersCount} open orders</>
    );

  return (
    <div
      onClick={() => onClick?.(id)}
      className={`
        group relative flex flex-col px-4 py-3
        rounded-xl border border-[var(--divider)]
        bg-bg-2
        cursor-pointer select-none
        transition-all duration-200
        hover:border-[var(--divider)] dark:hover:border-[var(--divider)]
        ${isStopped ? 'opacity-60' : ''}
      `}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(id);
        }
      }}
    >
      {/* --> Left accent bar */}
      <div className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-gradient-to-b ${modeCfg.accentBorder}`} />

      {/* --> Line 1: main info row */}
      <div className="flex items-center gap-3 pl-2">
        {/* Status dot */}
        <span className="relative flex items-center justify-center w-3 h-3 flex-shrink-0">
          {statusCfg.pulse && (
            <span className={`absolute inset-0 rounded-full ${statusCfg.dotClass} opacity-40 animate-ping`} />
          )}
          <span className={`relative w-2 h-2 rounded-full ${statusCfg.dotClass}`} />
        </span>

        {/* Name */}
        <span className="text-sm font-semibold text-fg-1 truncate">{name}</span>

        {/* Status label */}
        <span className={`text-[10px] font-medium ${statusCfg.textClass}`}>{statusLabel}</span>

        {/* Mode badge */}
        <span className={`ds-badge xs ${mode === 'live' ? 'profit' : 'info'}`}>
          {modeCfg.label}
        </span>

        {/* Timeframe */}
        <span className="text-xs text-fg-3">{timeframe}</span>

        {/* Version */}
        {version && (
          <>
            <span className="w-0.5 h-0.5 rounded-full bg-fg-3" />
            <span className="text-xs text-fg-3">{version}</span>
          </>
        )}

        {/* Symbol */}
        {(symbol || instrument) && (
          <span className="text-xs text-fg-2 font-mono ml-auto mr-2">
            {symbol || extractSymbol(instrument)}
          </span>
        )}

        {/* Sparkline */}
        <div className="flex-shrink-0">
          <Sparkline data={sparklineData} width={80} height={28} id={id} />
        </div>

        {/* Live price badge — always visible, streams live ticks */}
        <div className="flex-shrink-0">
          <LivePriceBadge deploymentId={id} compact showSymbol={false} />
        </div>

        {/* P&L */}
        <div className="flex-shrink-0 text-right min-w-[90px]">
          <span className={`text-sm font-bold ${pnlClass}`} data-testid="strategy-pnl">
            {formattedPnl}
          </span>
        </div>

        {/* Hover action buttons */}
        <div className="flex-shrink-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {status === 'running' ? (
            <button
              onClick={(e) => { e.stopPropagation(); /* TODO: stop */ }}
              className="p-1.5 rounded-lg text-fg-3 hover:text-warning hover:bg-warning/10 transition-colors"
              title="Stop strategy"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); /* TODO: resume */ }}
              className="p-1.5 rounded-lg text-fg-3 hover:text-profit hover:bg-profit/10 transition-colors"
              title="Resume strategy"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onClick?.(id); }}
            className="p-1.5 rounded-lg text-fg-3 hover:text-primary hover:bg-primary/10 transition-colors"
            title="View details"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* --> Line 2: position or metrics summary */}
      <div className="pl-8 mt-1.5 text-xs text-fg-3">
        {secondLine}
      </div>

      {/* Component: LIVE dead-instrument action-required reason */}
      {showOpenPositionReason && (
        <p className="pl-8 mt-1 text-xs font-medium text-warning" role="status">
          {OPEN_POSITION_COPY}
        </p>
      )}
    </div>
  );
}

export default AlgoStrategyListItem;
